import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { usePanel } from "@/shared/use-panel";
import { registerPanel } from "@/panel-registry";
import { pendingWebTarget, setPendingWebTarget } from "@/shared/network-actions";
import "./lanbrowser.css";

interface LanBrowserTarget {
  id: string;
  url: string;
  name?: string;
}

interface TargetsResult {
  allowPrivateHosts?: boolean;
  allowInternetHosts?: boolean;
  allowAllPorts?: boolean;
  hostAllowlist?: string[];
  hostBlocklist?: string[];
  allowedPorts?: number[];
  targets?: LanBrowserTarget[];
}

interface StartResult {
  sessionId: string;
  headless: boolean;
  browser: string;
  width: number;
  height: number;
}

interface BrowserEvent {
  type?: string;
  at?: string;
  url?: string;
  status?: number;
  error?: string;
  level?: string;
  text?: string;
}

interface SnapshotResult {
  image?: string | null;
  mime?: string;
  width: number;
  height: number;
  url: string;
  title: string;
  readyState: string;
  events?: BrowserEvent[];
}

interface DiagResult {
  url?: string;
  dns?: string[];
  port?: number;
  tcpOk?: boolean;
  tcpMs?: number;
  tlsOk?: boolean | null;
  httpStatus?: number | null;
  httpError?: string | null;
  errors?: string[];
  durationMs?: number;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function imageSrc(snapshot: SnapshotResult | null): string | null {
  if (!snapshot?.image) return null;
  if (snapshot.image.startsWith("data:image/")) return snapshot.image;
  return `data:${snapshot.mime || "image/jpeg"};base64,${snapshot.image}`;
}

const LanBrowserPanel: Component = () => {
  const panel = usePanel();
  // Pré-remplissage depuis le scanner de services (network-scan) : si une URL est
  // en attente, on l'injecte dans le champ puis on clear (consommation unique).
  const initialWeb = pendingWebTarget();
  if (initialWeb) setPendingWebTarget(null);
  const [url, setUrl] = createSignal(initialWeb?.url || "");
  const [targets, setTargets] = createSignal<TargetsResult | null>(null);
  const [session, setSession] = createSignal<StartResult | null>(null);
  const [snapshot, setSnapshot] = createSignal<SnapshotResult | null>(null);
  const [diag, setDiag] = createSignal<DiagResult | null>(null);
  const [busy, setBusy] = createSignal<string | null>(null);
  const [status, setStatus] = createSignal<PanelStatus | null>(null);
  const [autoRefresh, setAutoRefresh] = createSignal(true);
  const [typedText, setTypedText] = createSignal("");

  let refreshTimer: ReturnType<typeof setInterval> | undefined;
  let viewportRef: HTMLDivElement | undefined;

  const active = createMemo(() => !!session());
  const currentStatus = createMemo<PanelStatus | undefined>(() => {
    if (busy()) return { text: busy()!, level: "info" };
    return status() || panel.status();
  });
  const currentImage = createMemo(() => imageSrc(snapshot()));
  const eventRows = createMemo(() => (snapshot()?.events ?? []).slice(-80).reverse());

  async function loadTargets(): Promise<void> {
    if (!panel.enabled()) return;
    try {
      const res = await panel.tools.call<TargetsResult>("lanbrowser_targets", {}, { timeoutMs: 10_000 });
      setTargets(res);
    } catch (err) {
      setStatus({ text: `Targets: ${errorMessage(err)}`, level: "warn" });
    }
  }

  onMount(() => {
    void loadTargets();
  });

  createEffect(() => {
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = undefined;
    if (active() && autoRefresh()) {
      refreshTimer = setInterval(() => {
        void capture(false);
      }, 1600);
    }
  });

  onCleanup(() => {
    if (refreshTimer) clearInterval(refreshTimer);
    if (active()) void stop(true);
  });

  function requestParams(): Record<string, unknown> {
    const u = url().trim();
    return u ? { url: u } : {};
  }

  async function start(): Promise<void> {
    if (busy() || active()) return;
    setBusy("Demarrage du navigateur...");
    setStatus(null);
    setDiag(null);
    try {
      const res = await panel.tools.call<StartResult>(
        "lanbrowser_start",
        { ...requestParams(), width: 1365, height: 768 },
        { timeoutMs: 35_000 },
      );
      setSession(res);
      setStatus({ text: `${res.browser} headless actif`, level: "info" });
      await capture(true);
      viewportRef?.focus();
    } catch (err) {
      setStatus({ text: errorMessage(err), level: "danger" });
    } finally {
      setBusy(null);
    }
  }

  async function stop(silent = false): Promise<void> {
    if (!active()) return;
    if (!silent) setBusy("Arret...");
    try {
      await panel.tools.call("lanbrowser_stop", {}, { timeoutMs: 15_000 });
    } catch (err) {
      if (!silent) setStatus({ text: errorMessage(err), level: "warn" });
    } finally {
      setSession(null);
      setSnapshot(null);
      if (!silent) setBusy(null);
    }
  }

  async function navigate(): Promise<void> {
    if (!active()) {
      await start();
      return;
    }
    setBusy("Navigation...");
    setStatus(null);
    try {
      await panel.tools.call("lanbrowser_navigate", requestParams(), { timeoutMs: 25_000 });
      await capture(true);
      viewportRef?.focus();
    } catch (err) {
      setStatus({ text: errorMessage(err), level: "danger" });
    } finally {
      setBusy(null);
    }
  }

  async function capture(showBusy: boolean): Promise<void> {
    if (!active()) return;
    if (showBusy) setBusy("Capture...");
    try {
      const res = await panel.tools.call<SnapshotResult>(
        "lanbrowser_snapshot",
        { includeImage: true },
        { timeoutMs: 25_000 },
      );
      setSnapshot(res);
      if (res.url && res.url !== "about:blank") setUrl(res.url);
    } catch (err) {
      setStatus({ text: errorMessage(err), level: "danger" });
    } finally {
      if (showBusy) setBusy(null);
    }
  }

  async function runDiag(): Promise<void> {
    const params = requestParams();
    if (!params.url) {
      setStatus({ text: "URL requise.", level: "warn" });
      return;
    }
    setBusy("Diagnostic...");
    setStatus(null);
    try {
      const res = await panel.tools.call<DiagResult>("lanbrowser_diag", params, { timeoutMs: 25_000 });
      setDiag(res);
    } catch (err) {
      setStatus({ text: errorMessage(err), level: "danger" });
    } finally {
      setBusy(null);
    }
  }

  async function sendInput(params: Record<string, unknown>, refresh = true): Promise<void> {
    if (!active()) return;
    try {
      await panel.tools.call("lanbrowser_input", params, { timeoutMs: 10_000 });
      if (refresh) setTimeout(() => void capture(false), 250);
    } catch (err) {
      setStatus({ text: errorMessage(err), level: "danger" });
    }
  }

  function pointerCoords(e: MouseEvent | WheelEvent): { x: number; y: number } | null {
    const img = e.currentTarget as HTMLImageElement;
    const snap = snapshot();
    if (!snap) return null;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: Math.round(((e.clientX - rect.left) / rect.width) * snap.width),
      y: Math.round(((e.clientY - rect.top) / rect.height) * snap.height),
    };
  }

  function handleImageClick(e: MouseEvent): void {
    const coords = pointerCoords(e);
    if (!coords) return;
    viewportRef?.focus();
    void sendInput({ kind: "click", x: coords.x, y: coords.y });
  }

  function handleWheel(e: WheelEvent): void {
    const coords = pointerCoords(e);
    if (!coords) return;
    e.preventDefault();
    void sendInput({ kind: "scroll", x: coords.x, y: coords.y, deltaY: e.deltaY }, false);
  }

  function handleKeyDown(e: KeyboardEvent): void {
    if (!active()) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key.length === 1) {
      e.preventDefault();
      void sendInput({ kind: "text", text: e.key });
      return;
    }
    const allowed = new Set([
      "Enter",
      "Tab",
      "Backspace",
      "Delete",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ]);
    if (allowed.has(e.key)) {
      e.preventDefault();
      void sendInput({ kind: "key", key: e.key });
    }
  }

  async function sendTypedText(): Promise<void> {
    const text = typedText();
    if (!text) return;
    setTypedText("");
    await sendInput({ kind: "text", text });
  }

  const toolbar = (
    <>
      <button class="btn btn--xs btn--primary" disabled={!panel.enabled() || !!busy()} onClick={() => void navigate()}>
        {active() ? "Go" : "Start"}
      </button>
      <button class="btn btn--xs" disabled={!active() || !!busy()} onClick={() => void capture(true)}>
        Capture
      </button>
      <button class="btn btn--xs" disabled={!panel.enabled() || !!busy()} onClick={() => void runDiag()}>
        Diag
      </button>
      <button class="btn btn--xs btn--danger" disabled={!active() || !!busy()} onClick={() => void stop()}>
        Stop
      </button>
      <label class="lanbrowser-toggle">
        <input type="checkbox" checked={autoRefresh()} onChange={(e) => setAutoRefresh(e.currentTarget.checked)} />
        Live
      </label>
    </>
  );

  return (
    <PanelLayout title="LAN Browser" toolbar={toolbar} status={currentStatus()}>
      <div class="lanbrowser-panel">
        <div class="lanbrowser-addressbar">
          <select
            class="panel-filter-input lanbrowser-target-select"
            value=""
            disabled={!panel.enabled() || !!busy()}
            onChange={(e) => {
              const selected = targets()?.targets?.find((t) => t.id === e.currentTarget.value);
              if (selected) setUrl(selected.url);
              e.currentTarget.value = "";
            }}
          >
            <option value="">Targets</option>
            <For each={targets()?.targets ?? []}>
              {(target) => <option value={target.id}>{target.name || target.id}</option>}
            </For>
          </select>
          <input
            class="panel-filter-input lanbrowser-url"
            value={url()}
            placeholder="https://example.com ou http://192.168.1.1"
            spellcheck={false}
            onInput={(e) => setUrl(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void navigate();
            }}
          />
        </div>

        <div class="lanbrowser-workspace">
          <div
            class="lanbrowser-viewport"
            ref={viewportRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            aria-label="LAN browser viewport"
          >
            <Show
              when={currentImage()}
              fallback={
                <div class="lanbrowser-empty">
                  <span>{active() ? "Capture en attente" : "Session arretee"}</span>
                </div>
              }
            >
              {(src) => (
                <img
                  class="lanbrowser-frame"
                  src={src()}
                  alt={snapshot()?.title || "LAN browser"}
                  draggable={false}
                  onClick={handleImageClick}
                  onWheel={handleWheel}
                />
              )}
            </Show>
          </div>

          <aside class="lanbrowser-side">
            <div class="lanbrowser-meta">
              <div class="lanbrowser-title">{snapshot()?.title || "-"}</div>
              <div class="lanbrowser-url-text">{snapshot()?.url || "-"}</div>
              <div class="lanbrowser-kv">
                <span>Etat</span>
                <strong>{snapshot()?.readyState || "-"}</strong>
              </div>
              <div class="lanbrowser-kv">
                <span>Mode host</span>
                <strong>{session()?.headless ? "headless" : "-"}</strong>
              </div>
              <div class="lanbrowser-kv">
                <span>Acces</span>
                <strong>
                  {targets()?.allowInternetHosts ? "Internet" : "WAN off"} /{" "}
                  {targets()?.allowPrivateHosts ? "LAN" : "LAN off"}
                </strong>
              </div>
              <div class="lanbrowser-kv">
                <span>Ports</span>
                <strong>{targets()?.allowAllPorts ? "*" : (targets()?.allowedPorts ?? []).join(", ")}</strong>
              </div>
            </div>

            <div class="lanbrowser-text-send">
              <input
                class="panel-filter-input"
                value={typedText()}
                placeholder="Texte"
                disabled={!active()}
                onInput={(e) => setTypedText(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void sendTypedText();
                }}
              />
              <button class="btn btn--xs" disabled={!active() || !typedText()} onClick={() => void sendTypedText()}>
                Envoyer
              </button>
            </div>

            <div class="lanbrowser-keys">
              <For each={["Enter", "Tab", "Backspace", "Escape"]}>
                {(key) => (
                  <button class="btn btn--xs" disabled={!active()} onClick={() => void sendInput({ kind: "key", key })}>
                    {key}
                  </button>
                )}
              </For>
            </div>

            <Show when={diag()}>
              {(d) => (
                <div class="lanbrowser-diag">
                  <div class="lanbrowser-kv">
                    <span>DNS</span>
                    <strong>{d().dns?.length || 0}</strong>
                  </div>
                  <div class="lanbrowser-kv">
                    <span>TCP</span>
                    <strong>{d().tcpOk ? `${d().tcpMs ?? "-"} ms` : "fail"}</strong>
                  </div>
                  <div class="lanbrowser-kv">
                    <span>HTTP</span>
                    <strong>{d().httpStatus ?? d().httpError ?? "-"}</strong>
                  </div>
                  <Show when={(d().errors ?? []).length > 0}>
                    <div class="lanbrowser-error-list">
                      <For each={d().errors ?? []}>{(err) => <div>{err}</div>}</For>
                    </div>
                  </Show>
                </div>
              )}
            </Show>
          </aside>
        </div>

        <div class="lanbrowser-events">
          <div class="lanbrowser-events-title">Evenements</div>
          <Show when={eventRows().length > 0} fallback={<span class="lanbrowser-muted">Aucun evenement recent.</span>}>
            <For each={eventRows()}>
              {(ev) => (
                <div class="lanbrowser-event" data-type={ev.type || "event"}>
                  <span>{ev.type || "event"}</span>
                  <strong>{ev.status ?? ev.level ?? ""}</strong>
                  <code>{ev.url || ev.error || ev.text || ""}</code>
                </div>
              )}
            </For>
          </Show>
        </div>
      </div>
    </PanelLayout>
  );
};

registerPanel("lanbrowser", LanBrowserPanel);
